/**
 * Détection du geste de lancer — fonctions pures, testables sans DOM.
 *
 * Deux gestes valent un lancer depuis la v0.7.0 (COMMENTS v0.6.0 §2, arbitrage
 * A22 « le swipe est remplacé par un toucher simple ») :
 *
 *  - **le toucher** (`isTap`) : appui puis relâché en ≤ 300 ms et < 12 px de
 *    déplacement, **n'importe où sur l'écran de combat** (arène *et* bac) ;
 *  - **le swipe vers le haut** (`isValidSwipe`, règle historique
 *    ARCHITECTURE.md §4.2 : Δy ≤ −40 px écran, durée ≤ 600 ms) — conservé pour
 *    la compatibilité : un joueur (ou un test e2e) qui glisse encore vers le
 *    haut lance toujours ses dés.
 *
 * Un swipe est aussi un appui suivi d'un relâché : `isThrowGesture` accepte
 * donc **l'union** des deux, et les seuils sont disjoints (un swipe dépasse
 * toujours les 12 px du toucher), ce qui garantit **un seul** lancer par geste.
 *
 * La direction/longueur n'influent que sur la trajectoire visuelle du lancer
 * (spec V0 §4), jamais sur la validité du geste.
 */

export interface SwipePoint {
  /** Coordonnée écran (CSS px), ex. `event.clientX`. */
  x: number;
  /** Coordonnée écran (CSS px), ex. `event.clientY`. */
  y: number;
  /** Horodatage en millisecondes (ex. `performance.now()`). */
  t: number;
}

export interface SwipeThresholds {
  /** Déplacement vertical minimum (vers le haut) en px écran. */
  minDeltaY: number;
  /** Durée maximale du geste en millisecondes. */
  maxDurationMs: number;
}

export const DEFAULT_SWIPE_THRESHOLDS: SwipeThresholds = {
  minDeltaY: 40,
  maxDurationMs: 600,
};

export interface TapThresholds {
  /** Durée maximale entre l'appui et le relâché, en millisecondes. */
  maxDurationMs: number;
  /** Déplacement maximal toléré (px écran) — au-delà, c'est un glissé. */
  maxMovePx: number;
}

/** Toucher simple (A22) : bref et quasi immobile. */
export const DEFAULT_TAP_THRESHOLDS: TapThresholds = {
  maxDurationMs: 300,
  maxMovePx: 12,
};

/**
 * Point de geste ramené aux px de conception : la colonne agrandie sur grand
 * écran (`ui/columnScale.ts`, `transform: scale`) donne des coordonnées de
 * pointeur en px écran ; divisées par l'échelle, les seuils du toucher (12 px)
 * et du glissé (40 px) gardent le même sens à toute taille. Échelle 1 : le
 * point est inchangé.
 */
export function gesturePoint(
  e: { clientX: number; clientY: number },
  t: number,
  uiScale = 1,
): SwipePoint {
  const s = Number.isFinite(uiScale) && uiScale > 0 ? uiScale : 1;
  return { x: e.clientX / s, y: e.clientY / s, t };
}

/** Distance euclidienne entre deux points (px écran). */
export function pointerDistance(a: SwipePoint, b: SwipePoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/**
 * Un swipe est valide si le point de fin est au moins `minDeltaY` px
 * au-dessus du point de départ (Δy négatif = vers le haut), en au plus
 * `maxDurationMs` millisecondes.
 */
export function isValidSwipe(
  start: SwipePoint,
  end: SwipePoint,
  cfg: SwipeThresholds = DEFAULT_SWIPE_THRESHOLDS,
): boolean {
  const dt = end.t - start.t;
  if (dt < 0 || dt > cfg.maxDurationMs) return false;
  const dy = end.y - start.y;
  return dy <= -cfg.minDeltaY;
}

/**
 * Toucher simple (A22). `travel` est le déplacement **maximal** observé
 * pendant le geste (`pointermove`) : un aller-retour qui revient à son point
 * de départ reste un glissé, pas un toucher. Omis, il vaut la distance entre
 * les deux points.
 */
export function isTap(
  start: SwipePoint,
  end: SwipePoint,
  travel = pointerDistance(start, end),
  cfg: TapThresholds = DEFAULT_TAP_THRESHOLDS,
): boolean {
  const dt = end.t - start.t;
  if (dt < 0 || dt > cfg.maxDurationMs) return false;
  const moved = Math.max(travel, pointerDistance(start, end));
  return moved < cfg.maxMovePx;
}

/**
 * Le geste lance-t-il les dés ? Toucher **ou** swipe vers le haut (A22 +
 * compatibilité). Les deux seuils étant disjoints, un même geste ne peut pas
 * compter deux fois.
 */
export function isThrowGesture(
  start: SwipePoint,
  end: SwipePoint,
  travel = pointerDistance(start, end),
  tapCfg: TapThresholds = DEFAULT_TAP_THRESHOLDS,
  swipeCfg: SwipeThresholds = DEFAULT_SWIPE_THRESHOLDS,
): boolean {
  return isTap(start, end, travel, tapCfg) || isValidSwipe(start, end, swipeCfg);
}

/** Vecteur du geste (px écran), utilisé uniquement pour orienter l'arc visuel. */
export function swipeVector(start: SwipePoint, end: SwipePoint): { dx: number; dy: number } {
  return { dx: end.x - start.x, dy: end.y - start.y };
}

/**
 * Vecteur d'arc d'un lancer : le geste s'il a une vraie direction, sinon
 * « tout droit vers le haut » (cas du toucher, immobile par définition).
 */
export function throwVector(start: SwipePoint, end: SwipePoint): { dx: number; dy: number } {
  const v = swipeVector(start, end);
  return Math.hypot(v.dx, v.dy) < DEFAULT_TAP_THRESHOLDS.maxMovePx ? { dx: 0, dy: -1 } : v;
}

/**
 * Touche de lancer au clavier (phase 2, A78) : Espace ou Entrée (pavé
 * numérique compris), rien d'autre. Se lit sur `code`, indépendant de la
 * disposition du clavier.
 */
export function isThrowKey(e: { code: string }): boolean {
  return e.code === 'Space' || e.code === 'Enter' || e.code === 'NumpadEnter';
}

/** Éléments qui gardent Espace / Entrée quand ils ont le focus au clavier. */
const KEY_OWNER_SELECTOR =
  'button, a[href], input, select, textarea, summary, [role="button"], [role="tab"], [tabindex]:not([tabindex="-1"])';

/**
 * Une commande atteinte au clavier (Tab) garde Espace / Entrée : le lancer ne
 * doit pas l'écraser. Un bouton resté sous le focus après un clic de souris ne
 * compte pas (`:focus-visible` ne s'y applique pas) : Espace lance alors.
 */
export function keyboardOwnsKey(target: EventTarget | null): boolean {
  const el = target as { closest?: (selector: string) => Element | null } | null;
  if (!el || typeof el.closest !== 'function') return false;
  const owner = el.closest(KEY_OWNER_SELECTOR);
  if (!owner) return false;
  try {
    return owner.matches(':focus-visible');
  } catch {
    return true;
  }
}
